import React, { useCallback, useEffect, useMemo } from "react";
import { Col, Container, Row } from "reactstrap";
import {
  BOARD_CARD_LOADING,
  WEB_CHAT_LOADING,
} from "../../constants/loading.constant";
import {
  BOARD_CARD_MODAL,
  CONTACT_EXPAND_EDIT_FORM,
} from "../../constants/modal.constant";
import { useContactCore } from "../../cores/contacts";
import { useGlobalCore } from "../../cores/globals";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import { useIntl } from "react-intl";
import { useWebChatCore } from "../../cores/webChat";
import PanelChatTitle from "../component/chat/PanelChatTitle";
import WebChatForm from "../component/chat/web/WebChatForm";
import SocialChatForm from "../component/chat/social/SocialChatForm";
import ConversationList from "../component/chat/ConversationList";
import EditContactForm from "../component/contacts/modal/EditContactForm";
import { NoData } from "../component/NoData";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import SideModal from "../layout/SideModal";
import { getColor } from "../utils/stringUtils";
import TaskSideModals from "../component/tasks/TaskSideModals";
import ConstactSideModals from "../component/contacts/ContactSideModals";
import CompanySideModals from "../component/contactCompanies/CompanySideModals";
import EventSideModals from "../component/event/EventSideModals";
import EmailSideModals from "../component/gmail/EmailSideModals";
import BoardSideModals from "../component/boards/BoardSideModals";
import { useConfigCore } from "../../cores/config";
import { Errors } from "../../constants/errors";
import { CARD_PRODUCT } from "../../constants/internalModal.constant";
import { useBoardCore } from "../../cores/boards";
import { useSocialChatCore } from "../../cores/socialChat";

export default function EditChat() {
  const intl = useIntl();
  const {
    company,
    companyPlan,
    companyRole,
    isAdmin,
    isReseller,
    socialSettings,
  } = useMeCore();
  const { selected, getConversations, selectWebConversation } =
    useWebChatCore();
  const { getCommentTypes } = useConfigCore();
  const { goToSocial } = useRouterCore();
  const { cleanCard, errorCode } = useBoardCore();
  const { deleteContacts, getContactTags, getContact } = useContactCore();
  const {
    isModal,
    turnOffModal,
    turnOnModal,
    getModalVisibility,
    getModalContent,
    isLoading,
    turnOffInternalModal,
  } = useGlobalCore();
  const {
    facebookMessages,
    facebookSelected,
    instagramMessages,
    instagramSelected,
    whatsappMessages,
    whatsappSelected,
    selectFacebookConversation,
    selectInstagramConversation,
    selectWhatsappConversation,
    getFacebookConversations,
    getInstagramConversations,
    getWhatsappConversations,
    getFacebookMessages,
    postFacebookMessage,
    postFacebookSeenMessage,
    getInstagramMessages,
    postInstagramMessage,
    getWhatsappMessages,
    postWhatsappMessage,
  } = useSocialChatCore();

  useEffect(() => {
    if (
      !companyPlan?.rules.allowWebChat ||
      (!companyRole?.chat &&
        !companyRole?.owner &&
        !companyRole?.admin &&
        !companyRole?.agent &&
        !isAdmin &&
        !isReseller)
    )
      goToSocial();
  }, [companyPlan, companyRole, isAdmin, isReseller, goToSocial]);

  useEffect(() => {
    getConversations(company?.id!);
  }, [company, getConversations]);

  useEffect(() => {
    if (socialSettings?.facebook) getFacebookConversations(company?.id!);
  }, [company, socialSettings?.facebook, getFacebookConversations]);

  useEffect(() => {
    if (socialSettings?.instagramMensseger)
      getInstagramConversations(company?.id!);
  }, [company, socialSettings?.instagramMensseger, getInstagramConversations]);

  useEffect(() => {
    if (socialSettings?.whatsapp) getWhatsappConversations(company?.id!);
  }, [company, socialSettings?.whatsapp, getWhatsappConversations]);

  const onClickAvatar = useCallback(
    (contact) => {
      getContactTags(company?.id!);
      getCommentTypes(company?.id!);
      getContact(company?.id!, contact.id);
      turnOnModal(
        CONTACT_EXPAND_EDIT_FORM,
        <EditContactForm
          onDelete={async () => {
            await deleteContacts(company?.id!, [contact.id]);
            await getConversations(company?.id!);
            turnOffModal(CONTACT_EXPAND_EDIT_FORM);
          }}
          onUpdate={async () => {
            await getConversations(company?.id!);
            selectWebConversation(selected?.id!);
            selectWebConversation(selected?.id!);
          }}
        />
      );
    },
    [
      company,
      selected,
      turnOnModal,
      getCommentTypes,
      turnOffModal,
      getConversations,
      deleteContacts,
      getContactTags,
      selectWebConversation,
      getContact,
    ]
  );

  const chatForm = useMemo(() => {
    if (selected) return <WebChatForm />;
    if (facebookSelected)
      return (
        <SocialChatForm
          selected={facebookSelected}
          messages={facebookMessages}
          getMessages={getFacebookMessages}
          postMessage={postFacebookMessage}
          postSeenMessage={postFacebookSeenMessage}
        />
      );
    if (instagramSelected)
      return (
        <SocialChatForm
          selected={instagramSelected}
          messages={instagramMessages}
          getMessages={getInstagramMessages}
          postMessage={postInstagramMessage}
        />
      );
    if (whatsappSelected)
      return (
        <SocialChatForm
          selected={whatsappSelected}
          messages={whatsappMessages}
          getMessages={getWhatsappMessages}
          postMessage={postWhatsappMessage}
        />
      );
  }, [
    selected,
    instagramMessages,
    instagramSelected,
    facebookMessages,
    facebookSelected,
    whatsappMessages,
    whatsappSelected,
    getFacebookMessages,
    postFacebookMessage,
    postFacebookSeenMessage,
    getInstagramMessages,
    postInstagramMessage,
    getWhatsappMessages,
    postWhatsappMessage,
  ]);

  const onSelect = useCallback(
    (conversation) => {
      if (conversation.type === "web") {
        selectWebConversation(conversation.id);
        selectFacebookConversation(null);
        selectInstagramConversation(null);
        selectWhatsappConversation(null);
      }
      if (conversation.type === "facebook") {
        selectFacebookConversation(conversation.id);
        selectWebConversation(null);
        selectInstagramConversation(null);
        selectWhatsappConversation(null);
      }
      if (conversation.type === "instagram") {
        selectInstagramConversation(conversation.id);
        selectWebConversation(null);
        selectFacebookConversation(null);
        selectWhatsappConversation(null);
      }
      if (conversation.type === "whatsapp") {
        selectWhatsappConversation(conversation.id);
        selectWebConversation(null);
        selectFacebookConversation(null);
        selectInstagramConversation(null);
      }
    },
    [
      selectWebConversation,
      selectFacebookConversation,
      selectInstagramConversation,
      selectWhatsappConversation,
    ]
  );

  const selectedConv = useMemo(
    () => selected || facebookSelected || instagramSelected || whatsappSelected,
    [selected, facebookSelected, instagramSelected, whatsappSelected]
  );

  return (
    <Layout>
      <Header2 title={intl.formatMessage({ id: "app.chat.WembSiteTitle" })} />
      <Content>
        <Container fluid>
          <UpdateLoadingCard
            loadingId={WEB_CHAT_LOADING}
            text={intl.formatMessage({ id: "app.chat.UpdatingWebChats" })}
          />

          <Row>
            <Col xs={{ size: "12", order: 2 }} sm={{ size: "5", order: 1 }}>
              <ConversationList
                onSelect={(conversation) => onSelect(conversation)}
              />
            </Col>
            <Col
              xs={{ size: "12", order: 1 }}
              sm={{ size: "7", order: 2 }}
              className="overflow-auto"
            >
              <Container className="sticky-top p-0 z-index-50 mb-4">
                {!selectedConv ? (
                  <NoData
                    image="generic2"
                    text="Selecciona un contacto para comenzar la conversación"
                    horizontal
                  />
                ) : (
                  <>
                    <PanelChatTitle
                      color={getColor(parseInt(selectedConv.id))}
                      type={selectedConv.type}
                      contacts={selectedConv.participants}
                      avatarAction={
                        selectedConv.type === "web" ||
                        selectedConv.type === "whatsapp"
                          ? onClickAvatar
                          : () => null
                      }
                    />
                    {chatForm}
                  </>
                )}
              </Container>
            </Col>
          </Row>
        </Container>
      </Content>

      <TaskSideModals />
      <ConstactSideModals />
      <CompanySideModals />
      <BoardSideModals />
      <EventSideModals />
      <EmailSideModals />

      <SideModal
        active={isModal(BOARD_CARD_MODAL)}
        visible={getModalVisibility(BOARD_CARD_MODAL)}
        onClose={() => {
          turnOffModal(BOARD_CARD_MODAL);
          turnOffInternalModal(CARD_PRODUCT);
          cleanCard();
        }}
        showSppiner={isLoading(BOARD_CARD_LOADING)}
        showError={errorCode === Errors.CardLoad}
      >
        {getModalContent(BOARD_CARD_MODAL)}
      </SideModal>
    </Layout>
  );
}
